// src/pages/Leite/PropriedadePage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Grid, Link, Skeleton, Stack, Typography } from '@mui/material';
import * as Lucide from 'lucide-react';
import { buscarColetas, listarPontosColeta, ultimaColeta } from '../../infra/api/Coletas';
import { EVENTO_PLANOS_ALTERADOS } from '../../infra/api/PlanosAcao';
import { sugestoesAssistente, ultimaTemperatura } from '../../infra/api/Propriedade';
import usePaginasColeta from '../../hooks/usePaginasColeta';
import useResumoPlanosAcao from '../../hooks/useResumoPlanosAcao';
import { usePermissao } from '../../security/usePermissao';
import { PAGINAS_COLETA } from '../../utils/coleta';
import { AZUL_CLARO } from '../../theme/cores';
import ColetasAtivas from '../Coleta/components/ColetasAtivas';
import UltimaAnaliseCard from './propriedade/UltimaAnaliseCard';
import { AssistenteCard } from './propriedade/Assistente';
import IndicadorCard from './propriedade/IndicadorCard';
import TemperaturaDialog from './propriedade/TemperaturaDialog';
import { MARGEM_LATERAL, TITULO_PAGINA } from '../../theme/escalaPagina';

const ATUALIZAR_MS = 60_000;
// A IA redige em segundo plano e o servidor só espera 4 s: na primeira abertura costuma
// sair o texto da regra. Uma segunda busca das sugestões, logo depois, já traz o da IA.
const REBUSCAR_SUGESTOES_MS = 10_000;
const UM_DECIMAL = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const dois = (n) => String(n).padStart(2, '0');
const quando = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : `${dois(d.getDate())}/${dois(d.getMonth() + 1)} às ${dois(d.getHours())}:${dois(d.getMinutes())}`;
};
const maiuscula = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);
/** Lista de em andamento: o que esta rodando primeiro, e leite antes de colostro e agua. */
const ORDEM_PAGINAS = ['LEITE', 'COLOSTRO', 'AGUA'];
const ordenarAtivas = (lista) =>
    [...lista].sort(
        (a, b) =>
            (a.status === 'EM_PROCESSAMENTO' ? 0 : 1) - (b.status === 'EM_PROCESSAMENTO' ? 0 : 1) ||
            ORDEM_PAGINAS.indexOf(a.pagina) - ORDEM_PAGINAS.indexOf(b.pagina)
    );

/**
 * Minha propriedade — página inicial do plano LEITE. A última CBT e o que o assistente
 * sugere sobre ela; quantas análises estão rodando, a temperatura do tanque (registro do
 * produtor) e as ações pendentes; e as análises em andamento de leite, colostro e água.
 */
export default function PropriedadePage() {
    const { tenant } = useParams();
    const navigate = useNavigate();
    const { pode } = usePermissao();
    const { paginas, carregando: carregandoPaginas } = usePaginasColeta();
    const resumo = useResumoPlanosAcao(true);
    const leite = paginas.LEITE;

    const [ultima, setUltima] = useState(null);
    const [sugestoes, setSugestoes] = useState(null);
    const [temperatura, setTemperatura] = useState(null);
    const [ativas, setAtivas] = useState([]);
    const [pontos, setPontos] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [registrandoTemperatura, setRegistrandoTemperatura] = useState(false);

    // Depende só das CHAVES das páginas configuradas: o mapa de páginas é recriado quando o
    // bootstrap muda, e isso não pode disparar uma recarga do painel inteiro.
    const assinaturaPaginas = Object.keys(paginas).sort().join(',');
    const chavesPaginas = useMemo(() => (assinaturaPaginas ? assinaturaPaginas.split(',') : []), [assinaturaPaginas]);

    const carregar = useCallback(async () => {
        const [rUltima, rSugestoes, rTemperatura, rPontos, ...rAtivas] = await Promise.allSettled([
            chavesPaginas.includes('LEITE') ? ultimaColeta('LEITE') : Promise.resolve(null),
            sugestoesAssistente(),
            ultimaTemperatura(),
            listarPontosColeta({ incluirInativos: true }),
            ...chavesPaginas.map((pagina) =>
                buscarColetas({ pagina, status: ['PENDENTE', 'EM_PROCESSAMENTO'] }, 0, 20).then((r) =>
                    (r.items || []).map((item) => ({ ...item, pagina }))
                )
            ),
        ]);
        // Cada parte falha sozinha: o painel mostra o que conseguiu carregar.
        if (rUltima.status === 'fulfilled') setUltima(rUltima.value);
        setSugestoes(rSugestoes.status === 'fulfilled' ? rSugestoes.value : []);
        if (rTemperatura.status === 'fulfilled') setTemperatura(rTemperatura.value);
        if (rPontos.status === 'fulfilled') setPontos(rPontos.value || []);
        setAtivas(ordenarAtivas(rAtivas.flatMap((r) => (r.status === 'fulfilled' ? r.value : []))));
        setCarregando(false);
    }, [chavesPaginas]);

    const rebuscouSugestoes = useRef(false);
    useEffect(() => {
        if (rebuscouSugestoes.current || !sugestoes?.length || sugestoes.some((s) => s.redigidaPorIA)) return undefined;
        rebuscouSugestoes.current = true;
        const id = setTimeout(() => {
            sugestoesAssistente().then(setSugestoes).catch(() => {});
        }, REBUSCAR_SUGESTOES_MS);
        return () => clearTimeout(id);
    }, [sugestoes]);

    useEffect(() => {
        if (carregandoPaginas) return undefined;
        carregar();
        const id = setInterval(carregar, ATUALIZAR_MS);
        window.addEventListener(EVENTO_PLANOS_ALTERADOS, carregar);
        return () => {
            clearInterval(id);
            window.removeEventListener(EVENTO_PLANOS_ALTERADOS, carregar);
        };
    }, [carregar, carregandoPaginas]);

    const nomePonto = useCallback((id) => pontos.find((p) => p.id === id)?.nome || null, [pontos]);
    const abrirAmostra = (pagina, id) => {
        const meta = PAGINAS_COLETA[pagina];
        if (meta) navigate(`/${tenant}/${meta.rota}/${id}`);
    };

    const principal = sugestoes?.[0] || null;
    const daUltima = ultima ? (sugestoes || []).find((s) => s.amostraId === ultima.id) || null : null;
    const emAndamento = ativas.length;

    return (
        <Box sx={{ px: MARGEM_LATERAL, pt: { xs: 2, xl: 3 }, pb: { xs: 3, md: 4 }, maxWidth: 1500, mx: 'auto' }}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, fontWeight: 700 }}>
                Controle microbiológico
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ ...TITULO_PAGINA, mb: 3 }}>Minha propriedade</Typography>

            <Grid container spacing={2.5} alignItems="stretch">
                <Grid item xs={12} lg={7}>
                    <UltimaAnaliseCard
                        ultima={ultima}
                        nomePonto={nomePonto}
                        unidade={leite?.unidade || 'UFC/mL'}
                        rotuloMedida={leite?.rotuloMedida || 'CBT'}
                        sugestao={daUltima}
                        carregando={carregando || carregandoPaginas}
                        configurada={Boolean(leite)}
                        onAbrir={(u) => abrirAmostra('LEITE', u.id)}
                    />
                </Grid>
                <Grid item xs={12} lg={5}>
                    <AssistenteCard sugestao={principal} abertas={resumo?.abertas} carregando={sugestoes === null} />
                </Grid>

                <Grid item xs={12} md={4}>
                    <IndicadorCard
                        icone={<Lucide.Activity size={20} />}
                        cor={AZUL_CLARO}
                        rotulo="Análises em andamento"
                        valor={emAndamento}
                        carregando={carregando}
                        apoio="Acompanhe suas amostras"
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <IndicadorCard
                        icone={<Lucide.Thermometer size={20} />}
                        cor="#64748B"
                        rotulo="Temperatura do tanque"
                        valor={temperatura ? UM_DECIMAL.format(temperatura.valorC) : '—'}
                        carregando={carregando}
                        sufixo={temperatura ? '°C' : null}
                        rodape={
                            <Box sx={{ mt: 0.5 }}>
                                {temperatura ? (
                                    <>
                                        <Typography variant="body2" color="text.secondary">
                                            • Registro do produtor{temperatura.local ? ` · ${temperatura.local}` : ''}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">{quando(temperatura.dataHora)}</Typography>
                                    </>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">Nenhum registro ainda</Typography>
                                )}
                                {pode('dados.alterar') && (
                                    <Link component="button" type="button" underline="hover" display="block"
                                        onClick={() => setRegistrandoTemperatura(true)}
                                        sx={{ mt: 0.75, fontWeight: 700, fontSize: '0.85rem', color: AZUL_CLARO }}>
                                        Registrar temperatura
                                    </Link>
                                )}
                            </Box>
                        }
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <IndicadorCard
                        icone={<Lucide.ClipboardList size={20} />}
                        cor="#E0A43A"
                        rotulo="Ações pendentes"
                        valor={resumo ? resumo.abertas : '—'}
                        carregando={!resumo && carregando}
                        apoio={resumo?.vencidas ? `${resumo.vencidas} com prazo vencido` : 'Verificações e novas coletas'}
                        onClick={() => navigate(`/${tenant}/acoes`)}
                    />
                </Grid>
            </Grid>

            {carregando && (
                <Skeleton variant="rounded" height={92} sx={{ mt: 2.5, borderRadius: 3 }} aria-label="Carregando as análises em andamento" />
            )}

            {!carregando && ativas.length > 0 && (
                <Stack spacing={1.5} sx={{ mt: 2.5 }}>
                    <ColetasAtivas
                        ativas={ativas}
                        semRegistro={[]}
                        rotuloMedida=""
                        nomePonto={nomePonto}
                        podeAlterar={false}
                        onCancelar={() => {}}
                        onRegistrarExperimento={() => {}}
                        onAbrir={(a) => abrirAmostra(a.pagina, a.id)}
                        porItem={(a) => {
                            const meta = PAGINAS_COLETA[a.pagina];
                            return {
                                cor: meta?.cor,
                                pagina: maiuscula(meta?.nomeCurto),
                                rotuloMedida: paginas[a.pagina]?.rotuloMedida,
                                duracaoEstimadaHoras: meta?.duracaoEstimadaHoras,
                            };
                        }}
                    />
                </Stack>
            )}

            <TemperaturaDialog
                open={registrandoTemperatura}
                onClose={() => setRegistrandoTemperatura(false)}
                onSalvo={() => {
                    setRegistrandoTemperatura(false);
                    carregar();
                }}
            />
        </Box>
    );
}
